// Planar reflection for the still water that matters most on screen (water.ts).
//
// One small extra render per frame (q1: quarter res every other frame; q2/q3: half res every
// frame), from the camera mirrored about the chosen water plane, with an oblique near plane at
// the water height (Lengyel; same maths as three's Reflector) so nothing under the water leaks in.
// It draws ONLY the reflect layer: the sky dome + distant ranges, island tops, strata, landforms,
// tree crowns/trunks, the town hull and the falls (setCasters picks those meshes and tags them).
// No grass, particles, motes, birds, enemies, fx or HUD. Shadow maps are not re-rendered.
//
// Which water: ponds and slow stream stretches are candidate "bodies" (centre, radius, height),
// scored by apparent size (radius / distance) among those inside the view frustum and ~150 m.
// The winner's height is the mirror plane; the water shader uses the planar image only on
// still water within a metre or so of that height and falls back to its analytic sky elsewhere.
// No body in view -> no pass at all (uReflOn 0, the shader takes the fallback).

import * as THREE from 'three';
import type { World } from '../../../sim/world';

/** The layer the mirror camera sees. Nothing else in the build uses layers. */
export const REFLECT_LAYER = 7;

const RANGE = 150;
/** the town joins the mirror only when its street block is within this of the water's rim (per
 *  quality; at q2 that's water right by the town, at q3 the old 60 m) */
const TOWN_RANGE = [0, 0, 25, 60];
/** ?wdbg in the URL: log the mirror pass cost now and then (lab budget readout) */
const DEBUG = typeof location !== 'undefined' && location.search.includes('wdbg');

export class PlanarReflection {
  readonly uniforms: {
    uRefl: THREE.IUniform<THREE.Texture>;
    uReflMat: THREE.IUniform<THREE.Matrix4>;
    uReflY: THREE.IUniform<number>;
    uReflOn: THREE.IUniform<number>;
    uReflPx: THREE.IUniform<THREE.Vector2>;
  };
  /** last pass cost (draw calls, triangles) and whether it ran this frame: for the lab/budget */
  readonly stats = { calls: 0, tris: 0, ran: false, body: -1 };

  /** created lazily, the first time still water is in view (never on islands/frames without it) */
  private rt: THREE.WebGLRenderTarget | null = null;
  private cam = new THREE.PerspectiveCamera();
  private scale: number;
  private every: number;
  private q: number;
  /** candidate bodies: x, y, z, r per entry */
  private bodies: Float32Array;
  private nBodies: number;
  private cur = -1;
  private fade = 0;
  private lastTime = -1;
  private frame = 0;
  private roots: THREE.Object3D[] = [];
  private scanCountdown = 0;
  private kids = -1;
  private townMeshes: THREE.Mesh[] = [];
  private town: { x0: number; x1: number; z: number };
  private lightsTagged = false;

  // scratch (allocation-free per frame)
  private frustum = new THREE.Frustum();
  private pv = new THREE.Matrix4();
  private sphere = new THREE.Sphere();
  private camPos = new THREE.Vector3();
  private lookAt = new THREE.Vector3();
  private target = new THREE.Vector3();
  private rot = new THREE.Matrix4();
  private nrm = new THREE.Vector3(0, 1, 0);
  private planeP = new THREE.Vector3();
  private plane = new THREE.Plane();
  private clip = new THREE.Vector4();
  private qv = new THREE.Vector4();
  private size = new THREE.Vector2();

  constructor(world: World, quality: number) {
    this.q = quality;
    this.town = { x0: world.town.x0, x1: world.town.x1, z: world.town.z };
    this.scale = quality >= 2 ? 0.5 : 0.25;
    this.every = quality >= 2 ? 1 : 2;
    this.cam.layers.set(REFLECT_LAYER);
    this.cam.matrixAutoUpdate = true;
    this.uniforms = {
      uRefl: { value: null as unknown as THREE.Texture },
      uReflMat: { value: new THREE.Matrix4() },
      uReflY: { value: -1e4 },
      uReflOn: { value: 0 },
      uReflPx: { value: new THREE.Vector2(1 / 16, 1 / 16) },
    };

    // candidate bodies: every pond, plus slow stream stretches chunked to ~12 m
    const b: number[] = [];
    for (const p of world.ponds) b.push(p.x, p.y, p.z, p.r);
    for (const s of world.streams) {
      let ax = 0, az = 0, ay = 0, n = 0, L = 0, hw = 0;
      const flush = () => {
        if (n > 0 && L > 3) b.push(ax / n, ay / n, az / n, L * 0.5 + hw);
        ax = az = ay = 0; n = 0; L = 0; hw = 0;
      };
      for (const g of s.segs) {
        const slope = (g.ya - g.yb) / Math.max(0.1, g.len);
        if (slope > 0.035) { flush(); continue; }
        const mx = g.ax + g.ux * g.len * 0.5, mz = g.az + g.uz * g.len * 0.5;
        // stretches running through a pond belong to the pond
        let inPond = false;
        for (const p of world.ponds) if (Math.hypot(mx - p.x, mz - p.z) < p.r) { inPond = true; break; }
        if (inPond) { flush(); continue; }
        ax += mx; az += mz; ay += (g.ya + g.yb) * 0.5; n++; L += g.len; hw = Math.max(hw, g.hw);
        if (L >= 12) flush();
      }
      flush();
    }
    this.bodies = new Float32Array(b);
    this.nBodies = b.length / 4;
  }

  /** The meshes the mirror shows (roots are walked; Points, motes, grass etc. are skipped). */
  setCasters(roots: THREE.Object3D[]) {
    this.roots = roots;
    this.scanCountdown = 0;
  }

  private tagOne = (o: THREE.Object3D) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh || (o as unknown as THREE.Points).isPoints) return;
    const n = o.name;
    if (n === 'cloudSea' || n === 'mistBands' || n === 'birds' || n === 'townWires' || n === 'townCut' || n === 'ferns' || n === 'celestial') return;
    if (n === 'shrubs') return;
    // the far ranges are unnamed MeshBasic cards in the sky group: mirrored they come back as
    // teal blotches under a cloudless sky, so the mirror shows the sky there instead
    if (n === '' && (o as THREE.Mesh).renderOrder === -80) return;
    if (this.q < 2 && n.startsWith('treeTrunk')) return;
    if (n === 'townInk') return; // the town's ink hull doubles its triangles for a line nobody sees in a ripple
    // strata: the island undersides are almost all below the water plane; at q2 the mirror skips
    // them (31k triangles) and keeps them only at q3
    if (this.q < 3 && n === 'strata') return;
    if (this.q < 2 && ( n === 'townCanopy' || n === 'townBackdrop' || n === 'waterFalls')) return;
    // the town (~50k triangles) is mirrored only when the water is within ~60 m of it (render())
    if (n.startsWith('town')) { if (!this.townMeshes.includes(m)) this.townMeshes.push(m); return; }
    o.layers.enable(REFLECT_LAYER);
    if (DEBUG) {
      const g = m.geometry, n = (g.index ? g.index.count : g.attributes.position.count) / 3;
      console.log(`waterRefl caster ${n === n ? o.name || o.parent?.name : ''} tris ${Math.round(n)} x ${(m as THREE.InstancedMesh).count ?? 1}`);
    }
  };

  private scan(scene: THREE.Scene) {
    for (const r of this.roots) r.traverse(this.tagOne);
    if (!this.lightsTagged) {
      // lights are layer-tested too: the mirror needs the sun and the sky light
      for (const c of scene.children) if ((c as THREE.Light).isLight) c.layers.enable(REFLECT_LAYER);
      this.lightsTagged = true;
    }
  }

  /** Pick the body, render the mirror. Called from the water surface's onBeforeRender. */
  render(renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.Camera, time: number) {
    const persp = camera as THREE.PerspectiveCamera;
    if (!persp.isPerspectiveCamera) return;
    // one pass per frame however many times the scene is drawn
    if (time === this.lastTime) return;
    const dt = this.lastTime < 0 ? 0 : Math.min(0.1, Math.max(0, time - this.lastTime));
    this.lastTime = time;
    this.stats.ran = false;
    this.stats.calls = 0; this.stats.tris = 0;

    // re-tag when a root gains children (the trees arrive a moment after the island)
    let kids = 0;
    for (let i = 0; i < this.roots.length; i++) kids += this.roots[i].children.length;
    if (kids !== this.kids || this.scanCountdown-- <= 0) { this.kids = kids; this.scan(scene); this.scanCountdown = 300; }

    // ---- which body
    this.camPos.setFromMatrixPosition(camera.matrixWorld);
    this.pv.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    this.frustum.setFromProjectionMatrix(this.pv);
    const B = this.bodies;
    let best = -1, bestS = 0, curS = 0;
    for (let i = 0; i < this.nBodies; i++) {
      const x = B[i * 4], y = B[i * 4 + 1], z = B[i * 4 + 2], r = B[i * 4 + 3];
      if (this.camPos.y < y + 0.15) continue; // under (or at) this water: nothing to mirror
      const d = Math.hypot(x - this.camPos.x, y - this.camPos.y, z - this.camPos.z);
      if (d - r > RANGE) continue;
      this.sphere.center.set(x, y, z); this.sphere.radius = r;
      if (!this.frustum.intersectsSphere(this.sphere)) continue;
      const s = r / Math.max(d - r * 0.6, 2);
      if (i === this.cur) curS = s;
      if (s > bestS) { bestS = s; best = i; }
    }
    // hysteresis: keep the current body unless another clearly matters more
    if (this.cur >= 0 && curS > 0 && best !== this.cur && bestS < curS * 1.35) best = this.cur;
    const U = this.uniforms;
    this.stats.body = best;
    if (best < 0) {
      // nothing still in view: no pass at all, the shader takes its analytic sky
      U.uReflOn.value = 0;
      this.cur = -1;
      return;
    }
    if (best !== this.cur) {
      // switching to water at another height: fade the new mirror in rather than pop it
      // (first sight of a body pops straight in: it is at the edge of range or just came on screen)
      this.fade = this.cur >= 0 && Math.abs(B[best * 4 + 1] - B[this.cur * 4 + 1]) > 0.3 ? 0 : 1;
      this.cur = best;
      this.frame = 0;
    } else this.fade = Math.min(1, this.fade + dt * 3);
    U.uReflOn.value = this.fade;
    const py = B[best * 4 + 1];

    // q1: every other frame (the texture matrix stays paired with the image it was made for)
    if (this.frame++ % this.every !== 0 && U.uReflY.value === py) return;
    U.uReflY.value = py;

    // ---- the town only when it's near this water (beyond ~60 m it's a hazed smudge in a ripple)
    {
      // distance from this water's rim to the town's street block (world.town; the hull is one
      // merged mesh, so it's all or nothing)
      const t = this.town, bx = B[best * 4], bz = B[best * 4 + 2];
      const dx = Math.max(t.x0 - bx, bx - t.x1, 0), dz = Math.max(Math.abs(bz - t.z) - 20, 0);
      const on = Math.hypot(dx, dz) - B[best * 4 + 3] < TOWN_RANGE[this.q];
      for (let i = 0; i < this.townMeshes.length; i++) {
        if (on) this.townMeshes[i].layers.enable(REFLECT_LAYER); else this.townMeshes[i].layers.disable(REFLECT_LAYER);
      }
    }

    // ---- size (the target is made here, on first use)
    if (!this.rt) {
      this.rt = new THREE.WebGLRenderTarget(16, 16, {
        type: this.q >= 2 ? THREE.HalfFloatType : THREE.UnsignedByteType,
        depthBuffer: true, stencilBuffer: false, generateMipmaps: false,
        minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter,
      });
      this.rt.texture.name = 'waterReflection';
      U.uRefl.value = this.rt.texture;
    }
    renderer.getDrawingBufferSize(this.size);
    const w = Math.max(16, Math.round(this.size.x * this.scale)), h = Math.max(16, Math.round(this.size.y * this.scale));
    if (this.rt.width !== w || this.rt.height !== h) {
      this.rt.setSize(w, h);
      U.uReflPx.value.set(1 / w, 1 / h);
    }

    // ---- mirrored camera (plane y = py, normal +y)
    const c = this.cam, cp = this.camPos;
    c.position.set(cp.x, 2 * py - cp.y, cp.z);
    this.rot.extractRotation(camera.matrixWorld);
    this.lookAt.set(0, 0, -1).applyMatrix4(this.rot).add(cp);
    this.target.set(this.lookAt.x, 2 * py - this.lookAt.y, this.lookAt.z);
    c.up.set(0, 1, 0).applyMatrix4(this.rot);
    c.up.y = -c.up.y;
    c.lookAt(this.target);
    c.near = persp.near; c.far = persp.far;
    c.updateMatrixWorld();
    c.projectionMatrix.copy(camera.projectionMatrix);

    // texture matrix: world -> reflection uv (before the oblique clip, like Reflector)
    const tm = U.uReflMat.value;
    tm.set(0.5, 0, 0, 0.5, 0, 0.5, 0, 0.5, 0, 0, 0.5, 0.5, 0, 0, 0, 1);
    tm.multiply(c.projectionMatrix);
    tm.multiply(c.matrixWorldInverse);

    // oblique near plane a hand above the water: the lake beds sit just under the surface in
    // places, and a bed that pokes into the mirror reads as pale blotches across the reflection
    this.planeP.set(cp.x, py + 0.06, cp.z);
    this.plane.setFromNormalAndCoplanarPoint(this.nrm, this.planeP);
    this.plane.applyMatrix4(c.matrixWorldInverse);
    const cl = this.clip.set(this.plane.normal.x, this.plane.normal.y, this.plane.normal.z, this.plane.constant);
    const pm = c.projectionMatrix.elements;
    const qq = this.qv;
    qq.x = (Math.sign(cl.x) + pm[8]) / pm[0];
    qq.y = (Math.sign(cl.y) + pm[9]) / pm[5];
    qq.z = -1;
    qq.w = (1 + pm[10]) / pm[14];
    cl.multiplyScalar(2 / cl.dot(qq));
    pm[2] = cl.x; pm[6] = cl.y; pm[10] = cl.z + 1; pm[14] = cl.w;
    c.projectionMatrixInverse.copy(c.projectionMatrix).invert();

    // ---- render
    const prevRT = renderer.getRenderTarget();
    const prevShadow = renderer.shadowMap.autoUpdate;
    const prevXr = renderer.xr.enabled;
    const info = renderer.info.render;
    const c0 = info.calls, t0 = info.triangles;
    renderer.xr.enabled = false;
    renderer.shadowMap.autoUpdate = false;
    renderer.setRenderTarget(this.rt);
    renderer.state.buffers.depth.setMask(true);
    if (renderer.autoClear === false) renderer.clear();
    renderer.render(scene, c);
    this.stats.calls = info.calls - c0;
    this.stats.tris = info.triangles - t0;
    this.stats.ran = true;
    if (DEBUG && this.frame % 20 === 1) console.log(`waterRefl body ${best} y ${py.toFixed(2)} calls ${this.stats.calls} tris ${this.stats.tris} rt ${w}x${h}`);
    renderer.shadowMap.autoUpdate = prevShadow;
    renderer.xr.enabled = prevXr;
    // setRenderTarget restores that target's own viewport (the post pass target or the canvas)
    renderer.setRenderTarget(prevRT);
  }

  dispose() { this.rt?.dispose(); }
}
